import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://makeupbrushbasics.com";

/**
 * StructuredData — injects one or more JSON-LD blocks into <head>.
 *
 * Usage:
 *   <StructuredData schemas={[orgSchema, productSchema, howToSchema]} />
 *
 * Each schema is a plain JS object. Falsy items are skipped.
 */
export default function StructuredData({ schemas }) {
  const list = (Array.isArray(schemas) ? schemas : [schemas]).filter(Boolean);
  if (!list.length) return null;
  return (
    <Helmet>
      {list.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

// ── Reusable schema builders ────────────────────────────────

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "BrushBasics",
  url: SITE,
  logo: `${SITE}/images/og-default.jpg`,
  description:
    "An editorial guide to every makeup brush — what each does, and exactly how to use it.",
  sameAs: [
    // Add your real social URLs here when live
    // "https://www.pinterest.com/brushbasics",
    // "https://www.instagram.com/brushbasics",
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "BrushBasics",
  url: SITE,
  description:
    "The illustrated guide to 30 makeup brushes — what each does, how to use it, and which one belongs in your kit.",
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE}/catalog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE}${item.path}`,
    })),
  };
}

export function productSchema(brush) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: brush.name,
    image: brush.image?.startsWith("http") ? brush.image : `${SITE}${brush.image}`,
    description: brush.what || brush.tagline,
    brand: { "@type": "Brand", name: "BrushBasics" },
    category: brush.category ? `Makeup Brushes / ${brush.category}` : "Makeup Brushes",
    url: `${SITE}/brush/${brush.id}`,
  };
}

export function howToSchema(brush) {
  const steps = Array.isArray(brush.how_to_use) ? brush.how_to_use : [];
  if (!steps.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `How to use a ${brush.name}`,
    description: brush.what || brush.tagline,
    image: brush.image?.startsWith("http") ? brush.image : `${SITE}${brush.image}`,
    tool: [{ "@type": "HowToTool", name: brush.name }],
    step: steps.map((text, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: `Step ${i + 1}`,
      text,
    })),
  };
}

export function faqSchema(brush) {
  const tips = Array.isArray(brush.technique_tips) ? brush.technique_tips : [];
  if (!tips.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `What is a ${brush.name} used for?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: brush.what || brush.tagline,
        },
      },
      {
        "@type": "Question",
        name: `What's the best technique for using a ${brush.name}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: tips.join(" "),
        },
      },
      brush.care
        ? {
            "@type": "Question",
            name: `How do I care for my ${brush.name}?`,
            acceptedAnswer: { "@type": "Answer", text: brush.care },
          }
        : null,
    ].filter(Boolean),
  };
}

export const courseSchema = {
  "@context": "https://schema.org",
  "@type": "Course",
  name: "30 Brushes in 30 Days",
  description:
    "An emailed makeup brush masterclass. One brush, one lesson, delivered to your inbox every morning for a month.",
  provider: { "@type": "Organization", name: "BrushBasics", sameAs: SITE },
  url: `${SITE}/thirty-day`,
  hasCourseInstance: {
    "@type": "CourseInstance",
    courseMode: "online",
    courseWorkload: "PT10M",
  },
  offers: {
    "@type": "Offer",
    price: "19.99",
    priceCurrency: "USD",
    category: "Digital Course",
    availability: "https://schema.org/InStock",
  },
};
